import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GripVertical, Pencil, Plus } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Area, Card, DeleteBtn, Field, IconBtn, ImageField, Input, PageTitle, Select, Skel, StringList, Toggle, YBtn, useDragSort, useSaver } from "@/admin/ui";
import { adminApi, slugify, useAdmin, useRefresh } from "@/admin/lib";
import { inr } from "@/lib/api";

const CYCLES = ["one-time", "monthly", "yearly"];
const ICON_HINT = "Lucide icon name, e.g. Magnet, Calculator, Building2, Bot, Code2, Smartphone";

export const SCHEMAS = {
  products: {
    title: "Products", label: (x) => x.title, sub: (x) => `${x.category || ""} ${x.price ? `· ${inr(x.price)}` : ""}`, slugFrom: "title",
    fields: [["title", "Title"], ["slug", "Slug"], ["tagline", "Tagline"], ["category", "Category"], ["icon", "Icon", "text", ICON_HINT], ["price", "Price (INR, blank = no online purchase)", "number"],
      ["billing_cycle", "Billing cycle", "select", CYCLES], ["buy_enabled", "Show 'Buy Now' button", "switch"], ["image", "Image", "image"], ["short_description", "Short description", "textarea"],
      ["description", "Full description", "textarea"], ["features", "Features", "list"], ["benefits", "Benefits", "list"], ["seo_title", "SEO title"], ["seo_description", "SEO description", "textarea"]],
    blank: { buy_enabled: true, billing_cycle: "yearly", features: [], benefits: [], icon: "Sparkles" },
  },
  services: {
    title: "Services", label: (x) => x.title, sub: (x) => x.short_description, slugFrom: "title",
    fields: [["title", "Title"], ["slug", "Slug"], ["icon", "Icon", "text", ICON_HINT], ["short_description", "Short description", "textarea"], ["description", "Description", "textarea"], ["features", "Features", "list"]],
    blank: { features: [], icon: "Code2" },
  },
  plans: {
    title: "Pricing Plans", label: (x) => x.name, sub: (x) => (x.price ? `${inr(x.price)} · ${x.billing_cycle}` : "Custom quote") + (x.popular ? " · Popular" : ""),
    fields: [["name", "Plan name"], ["price", "Price (INR, blank = custom quote)", "number"], ["billing_cycle", "Billing cycle", "select", CYCLES], ["cta_type", "Button", "select", [{ value: "buy", label: "Buy Now" }, { value: "contact", label: "Contact Us" }]],
      ["popular", "Popular badge", "switch"], ["description", "Description", "textarea"], ["features", "Features", "list"]],
    blank: { billing_cycle: "monthly", cta_type: "buy", features: [], popular: false },
  },
  testimonials: {
    title: "Testimonials", label: (x) => x.name, sub: (x) => `${x.role || ""}, ${x.company || ""}`,
    fields: [["name", "Name"], ["role", "Role"], ["company", "Company"], ["rating", "Rating (1-5)", "number"], ["quote", "Quote", "textarea"]],
    blank: { rating: 5 },
  },
  faqs: {
    title: "FAQs", label: (x) => x.question, sub: (x) => `Category: ${x.category}`,
    fields: [["question", "Question"], ["category", "Category", "select", ["home", "contact", "pricing"]], ["answer", "Answer", "textarea"]],
    blank: { category: "home" },
  },
};

const FieldInput = ({ f, value, onChange }) => {
  const [key, label, type = "text", extra] = f;
  const tid = `item-field-${key}`;
  if (type === "switch") return <Toggle label={label} checked={value} onChange={onChange} testId={tid} />;
  const control = {
    textarea: <Area rows={key === "description" ? 5 : 3} value={value || ""} onChange={(e) => onChange(e.target.value)} testId={tid} />,
    select: <Select value={value || ""} onChange={(e) => onChange(e.target.value)} options={extra || []} testId={tid} />,
    list: <StringList value={value || []} onChange={onChange} testId={tid} />,
    image: <ImageField value={value} onChange={onChange} testId={tid} />,
    number: <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} testId={tid} />,
  }[type] || <Input value={value || ""} onChange={(e) => onChange(e.target.value)} testId={tid} />;
  return <Field label={label} hint={type === "text" ? extra : undefined}>{control}</Field>;
};

const EditSheet = ({ resource, schema, item, onClose, onSaved }) => {
  const [d, setD] = useState(item);
  const [saving, run] = useSaver();
  const isNew = !item.id;
  const set = (k, v) => setD((x) => ({ ...x, [k]: v, ...(isNew && schema.slugFrom === k ? { slug: slugify(v || "") } : {}) }));
  const save = async () => {
    const body = { ...d };
    ["id", "created_at", "updated_at"].forEach((k) => delete body[k]);
    const r = await run(() => (isNew ? adminApi.post(`/admin/cms/${resource}`, body) : adminApi.put(`/admin/cms/${resource}/${d.id}`, body)));
    if (r) { onSaved(); onClose(); }
  };
  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-full overflow-y-auto bg-white sm:max-w-xl" data-testid="item-edit-sheet">
        <SheetTitle className="font-heading text-xl text-navy">{isNew ? "Add" : "Edit"} {schema.title.replace(/s$/, "")}</SheetTitle>
        <div className="mt-6 space-y-4">
          {schema.fields.map((f) => <FieldInput key={f[0]} f={f} value={d[f[0]]} onChange={(v) => set(f[0], v)} />)}
          <Toggle label="Visible on website" checked={d.published !== false} onChange={(v) => set("published", v)} testId="item-field-published" />
          <YBtn loading={saving} onClick={save} className="w-full" testId="item-save-btn">Save</YBtn>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default function Collection({ resource }) {
  const schema = SCHEMAS[resource];
  const { data, isLoading } = useAdmin(`/admin/cms/${resource}`);
  const refresh = useRefresh();
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  useEffect(() => setItems(data || []), [data]);
  const reload = () => refresh(`/admin/cms/${resource}`);

  const reorder = async (next) => {
    setItems(next);
    try { await adminApi.post(`/admin/reorder/${resource}`, { ids: next.map((x) => x.id) }); toast.success("Order saved"); reload(); } catch { toast.error("Could not save order"); }
  };
  const drag = useDragSort(items, reorder);
  const toggle = async (x, v) => { setItems(items.map((i) => (i.id === x.id ? { ...i, published: v } : i))); await adminApi.put(`/admin/cms/${resource}/${x.id}`, { published: v }); reload(); };

  return (
    <>
      <PageTitle title={schema.title} subtitle="Drag rows to reorder. Toggle visibility without deleting.">
        <YBtn onClick={() => setEdit({ published: true, ...schema.blank })} testId={`${resource}-add-btn`}><Plus className="h-4 w-4" />Add new</YBtn>
      </PageTitle>
      <Card>
        {isLoading ? <Skel /> : (
          <div className="space-y-2" data-testid={`${resource}-list`}>
            {items.map((x, i) => (
              <div key={x.id} {...drag(i)} className="flex items-center gap-3 rounded-xl border border-line bg-white px-3 py-3 transition-shadow hover:shadow-soft" data-testid={`${resource}-row-${i}`}>
                <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-muted2" />
                <div className="min-w-0 flex-1"><p className="truncate font-semibold text-navy">{schema.label(x)}</p><p className="truncate text-xs text-muted2">{schema.sub(x)}</p></div>
                <Switch checked={x.published !== false} onCheckedChange={(v) => toggle(x, v)} aria-label="Visible" data-testid={`${resource}-visible-${i}`} />
                <IconBtn label="Edit" onClick={() => setEdit(x)} testId={`${resource}-edit-${i}`}><Pencil className="h-4 w-4" /></IconBtn>
                <DeleteBtn onConfirm={async () => { await adminApi.delete(`/admin/cms/${resource}/${x.id}`); toast.success("Deleted"); reload(); }} testId={`${resource}-delete-${i}`} />
              </div>
            ))}
            {!items.length && <p className="py-10 text-center text-sm text-muted2">Nothing here yet.</p>}
          </div>
        )}
      </Card>
      {edit && <EditSheet resource={resource} schema={schema} item={edit} onClose={() => setEdit(null)} onSaved={reload} />}
    </>
  );
}
